import React, { useEffect, useState } from 'react';
import Text from '@/components/Text';
import Button from '@/components/Button';
import { QUIZ_CHAPTERS } from '../../constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFire,
  faArrowRightLong,
  faDiamond as faDiamondSolid,
  faStar as faStarSolid,
  faBolt as faBoltSolid,
  faCompass as faCompassSolid,
  faSparkles
} from '@fortawesome/pro-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { twMerge } from 'tailwind-merge';

interface QuizCheckpointProps {
  chapterId: number;
  dominantValue: 'A' | 'B' | 'C' | 'D';
  onContinue: () => void;
}

const CHAPTER_STONES = [
  { icon: faDiamondSolid, color: 'text-emerald-400', bg: 'bg-emerald-400', glow: 'shadow-emerald-500/50', label: 'Decision' },
  { icon: faFire, color: 'text-rose-400', bg: 'bg-rose-400', glow: 'shadow-rose-500/50', label: 'Risk' },
  { icon: faCompassSolid, color: 'text-sky-400', bg: 'bg-sky-400', glow: 'shadow-sky-500/50', label: 'Research' },
  { icon: faBoltSolid, color: 'text-violet-400', bg: 'bg-violet-400', glow: 'shadow-violet-500/50', label: 'Management' },
  { icon: faStarSolid, color: 'text-amber-400', bg: 'bg-amber-400', glow: 'shadow-amber-500/50', label: 'Values' },
];

export default function QuizCheckpoint({ chapterId, dominantValue, onContinue }: QuizCheckpointProps) {
  const [mounted, setMounted] = useState(false);
  const chapter = QUIZ_CHAPTERS.find(c => c.id === chapterId);
  const stone = CHAPTER_STONES[chapterId - 1];

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!chapter || !stone) return <div />;

  const feedback = chapter.feedbacks[dominantValue];

  return (
    <div className="flex flex-col h-full md:h-auto min-h-[500px] md:min-h-0 max-w-xl mx-auto relative">
      {/* Scrollable Content Area */}
      <div className="flex-1 flex flex-col items-center justify-center gap-10 md:gap-12 py-0 md:py-8 px-0 md:px-6 text-center pb-48 md:pb-12 overflow-visible">
        {/* Background Particles/Glow */}
        <div className={twMerge(
          "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 size-[300px] md:size-[500px] blur-[80px] md:blur-[120px] opacity-20 -z-10 transition-colors duration-1000",
          stone.bg
        )} />
        <div className="flex flex-col gap-1 md:gap-2 relative z-10 animate-in fade-in slide-in-from-top-8 duration-700 mb-2 md:mb-4">
          {/* @ts-ignore */}
          <Text size="xs" className="text-white/40 uppercase tracking-[0.4em] font-bold text-[10px] md:text-xs">
            {chapter.title.split(': ')[1]}
          </Text>
          <div className="flex items-center justify-center gap-2 md:gap-4">
            <div className="h-px w-8 md:w-12 bg-gradient-to-r from-transparent to-white/20" />
            <h2 className="text-xl md:text-4xl font-sans font-black text-white uppercase tracking-tighter">
              {feedback.label}
            </h2>
            <div className="h-px w-8 md:w-12 bg-gradient-to-l from-transparent to-white/20" />
          </div>
        </div>

        <div className="relative w-full group mt-6 md:mt-0">
          {/* Floating Stone Display */}
          <div className="absolute -top-14 left-1/2 -translate-x-1/2 z-20 transition-transform duration-1000 group-hover:scale-110">
            <div className={twMerge(
              "size-16 md:size-20 rounded-2xl md:rounded-3xl flex items-center justify-center bg-[#111] border-2 border-white/10 shadow-2xl animate-bounce",
              stone.glow
            )}>
              <FontAwesomeIcon icon={stone.icon as IconProp} className={twMerge("size-8 md:size-10", stone.color)} />
              {/* Internal Glow */}
              <div className={twMerge("absolute inset-2 blur-xl opacity-30", stone.bg)} />
            </div>
          </div>

          {/* Roast Card */}
          <div className="bg-[#0A0A0A]/80 backdrop-blur-2xl p-4 pt-10 md:p-6 md:pt-12 rounded-[2rem] md:rounded-[3rem] border border-white/10 relative overflow-hidden shadow-[0_0_50px_rgba(0,0,0,0.5)] mt-6 md:mt-4 mx-4 md:mx-0">
            {/* Animated border/glow */}
            <div className={twMerge(
              "absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-current to-transparent opacity-50",
              stone.color
            )} />

            <div className="flex flex-col gap-4 md:gap-6 relative z-10">
              <div className="flex items-center justify-center gap-3 text-teal-200">
                <FontAwesomeIcon icon={faSparkles as IconProp} className="size-3 md:size-4 animate-spin-slow" />
                {/* @ts-ignore */}
                <Text size="xs" className="uppercase tracking-[0.3em] font-black text-teal-200/80 text-xs md:text-xs">
                  What This Means
                </Text>
                <FontAwesomeIcon icon={faSparkles as IconProp} className="size-3 md:size-4 animate-spin-slow" />
              </div>

              <span
                className="text-sm md:text-base inherit leading-relaxed text-white font-sans font-normal tracking-tight px-0 md:px-2"
                dangerouslySetInnerHTML={{ __html: feedback.roast }}
              />

            </div>

            {/* Decorative elements */}
            <div className={twMerge("absolute -top-24 -left-24 size-48 rounded-full blur-[80px] opacity-10", stone.bg)} />
            <div className={twMerge("absolute -bottom-24 -right-24 size-48 rounded-full blur-[80px] opacity-10", stone.bg)} />
          </div>
        </div>
      </div>

      <div className="fixed md:relative bottom-0 md:bottom-auto left-0 md:left-auto w-full z-50 md:z-10 animate-in fade-in slide-in-from-bottom-8 duration-700 delay-300 md:mb-12 md:px-6">
        <div className="flex flex-col items-center gap-0 w-full max-w-md md:max-w-none mx-auto group/next shadow-[0_-20px_40px_rgba(0,0,0,0.5)] md:shadow-none">
          <div className="bg-[#111] md:bg-white/5 backdrop-blur-xl md:backdrop-blur-sm p-5 pb-8 md:p-8 rounded-t-[2.5rem] md:rounded-[3rem] border-t md:border border-white/10 w-full relative overflow-hidden transition-all duration-500 hover:border-teal-200/30 flex flex-col gap-5 items-center">
            {/* Subtle glow accent */}
            <div className={twMerge(
              "absolute -bottom-12 -left-12 size-32 rounded-full blur-3xl opacity-10",
              stone.bg
            )} />
            <div className="flex flex-col items-center gap-2 md:gap-4 relative z-10 w-full">
              <div className="flex items-center gap-3">
                <div className="h-px w-6 bg-teal-200/20" />
                {/* @ts-ignore */}
                <Text size="xs" className="text-teal-200/80 uppercase tracking-[0.3em] font-black text-[10px] md:text-xs">
                  Next Phase
                </Text>
                <div className="h-px w-6 bg-teal-200/20" />
              </div>
              <p
                className="text-white text-md md:text-lg leading-relaxed font-bold tracking-tight text-center px-4"
                dangerouslySetInnerHTML={{ __html: feedback.transition }}
              />
            </div>

            <button
              onClick={onContinue}
              className={twMerge(
                "relative w-full py-4 md:py-6 rounded-full font-black text-sm md:text-base uppercase tracking-[0.3em] transition-all duration-500",
                "bg-white text-black hover:bg-teal-200 hover:shadow-[0_0_40px_rgba(153,246,228,0.3)] hover:-translate-y-1 active:scale-95 z-20 flex items-center justify-center gap-4 group/btn"
              )}
            >
              <span>Continue</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

